/**
 * model/resolveScene.ts — EXPLICIT DEPENDENCE CHAIN STEP 0→6 (kinetic chain).
 *
 * Resolves the ENTIRE 3D scene constrained from features (SSOT) and form.
 * Produces a `ResolvedScene` with coordinates already in mm in the viewer system.
 *
 * MODEL (SPEC §6 point 7 — KINEMATIC CHAIN, mechanical arms):
 * The system is an Object3D hierarchy of three.js: each frame is relative to the
 * parent, constraints are joints that transform child links. This is where we build
 * the HIERARCHY (root FramePlane → pivot alphaDeg → joist/header groups →
 * beams/fasteners) and extract the WORLD transforms after updateMatrixWorld.
 *
 * ```
 * planeFrame (FramePlane, y = h) ── root (SPEC G1)
 * ├─ headerBeamGroup (beam header, does NOT rotate with alphaDeg)
 * ├─ headerGroup(i) (lock header: quat = Rz(180°) ∘ conversion — G3)
 * └─ joistPivot (JOINT alphaDeg: quat = Rz(alphaDeg), G6)
 * ├─ joistBeamGroup (joist beam — rotate with alphaDeg)
 * └─ joistGroup(i) (lock joist: quat = conversion — holesQuat)
 * ```
 *
 * The op1–op8 “operation queue” of the design (KB document) BECOMES the
 * hierarchy: op3 (position joist) = child joist group of the pivot;
 * op4 (header derivation) = header group with Rz(180°)∘frame joist;
 * op5 (alphaDeg) = joist chain pivot; op6 (hole→fastener) = localToWorld;
 * op7 (notch) = Δshift separated field; op8 (beam) = beam groups.
 *
 * CHAIN ORDER (SPEC §4.5, AC-8):
 * STEP 0 features JSON (authoring: component on X-Y plane, normal holes along Z)
 * STEP 1 ROTATION CONVERSION Ry(∓90°) [sign SSOT config R1]
 * STEP 2 JOIST POSITIONING (flush G5/C1) — child group of the pivot
 * STEP 3 HEADER DERIVATION: Rz(180°) at FRAME level
 * STEP 4 alphaDeg: JOINT pivot — ONLY rotate the joist chain (G6/AC-2)
 * STEP 5 holes → fastener anchors (frame.localToWorld; axis = hole.normal) ← BEFORE
 * STEP 6 notch (CutTarget, thickness = connector_assembly.thickness)
 * → produces Δshift as a SEPARATE field (DOES NOT change position; applied in render)
 *
 * Boundary Rule (SPEC §6, AC-8): Here is ALL the dependency logic;
 * render components (T14) receive already resolved objects and DO NOT compute
 * nothing. Constrained 3D configurator: each STEP is commented for maintainability.*/

import * as THREE from "three";
import { DEFAULT_BEAM_LENGTH_MM } from "../config/defaults";
import type { GlbFeatures, Vec3 } from "../data/types";
import { createConnectorPlaneFrame } from "../transforms/connectorPlane";
import { clamp } from "../utils/math";
import type { BeamInput } from "./beams";
import { notchShiftFor, resolveBeam } from "./beams";
import type { ConnectorGroupInput } from "./connectors";
import { computeStagger, nConnectorsOffsetsZ, placeConnectorGroup } from "./connectors";
import { resolveAssemblyGap, resolveCutTarget } from "./cutTarget";
import type { FastenerInput } from "./fasteners";
import {
  buildFastenerLegend,
  fastenerDiameterFor,
  fastenerItemIdFor,
  fastenerLengthFor,
  holeToFastenerAnchor,
} from "./fasteners";
import type { BeamDims, ConnectorInstance, FastenerAnchor, ResolvedScene } from "./types";

/**
 * Sub-set of form values used by 3D (PLAN §2.15).
 * Consistent with `LockCalculatorFormValues` (SPEC §3.2) — here ONLY fields that
 * enter the constraint system.*/
export interface ResolveSceneFormInput {
  /** lateralResistanceConfig (0–4 o null) → CutTarget (config/cutTargets.ts). */
  lateralResistanceConfig: number | null;
  /** 'beam' | 'column' (header beam axis, SPEC G8).*/
  headerOrientation: string | null;
  /** Number of connector pairs (1 | 2; default 1).*/
  nConnectors: number | null;
  /** Header beam width (mm).*/
  headerB: number | null;
  /** Header beam height (mm).*/
  headerH: number | null;
  /** Joist beam width (mm).*/
  joistB: number | null;
  /** Joist beam height (mm).*/
  joistH: number | null;
  /** Joist chain rotation in degrees (SPEC G6).*/
  alphaDeg: number | null;
  /** Joist X-Z shear in degrees — same family as alphaDeg, independent axis.*/
  betaDeg: number | null;
  /** Distance (mm) from the beams' top face down to the connector assembly's
   * top — moves joist and header connectors rigidly along -Y; beams stay.
   * null/undefined = flush (0).*/
  connectorTopDistanceMm?: number | null;
  /** Distance (mm) the JOIST top sits below the header top (column header
   * only). The joist moves down by it and the connector assembly follows, so
   * the connector-to-joist-top distance is unchanged. null/undefined = flush.*/
  joistTopOffsetMm?: number | null;
  /** Header side fastener.*/
  headFastenerItemId: string | null;
  /** Fastener on joist side.*/
  joistFastenerItemId: string | null;
  /**Fastener for inclined holes.*/
  inclinedFastenerItemId: string | null;
  /** Real length (mm) of the header-side fastener, from the catalog Fastener
   * item's own `length` — null when unresolved, falls back to
   * config.defaultFastenerLength.*/
  headFastenerLengthMm: number | null;
  /** Real length (mm) of the joist-side fastener — see headFastenerLengthMm.*/
  joistFastenerLengthMm: number | null;
  /** Real length (mm) of the inclined fastener — see headFastenerLengthMm.*/
  inclinedFastenerLengthMm: number | null;
  /** Real diameter (mm) of the header-side fastener (catalog item).*/
  headFastenerDiameterMm: number | null;
  /** Real diameter (mm) of the joist-side fastener.*/
  joistFastenerDiameterMm: number | null;
  /** Real diameter (mm) of the inclined fastener.*/
  inclinedFastenerDiameterMm: number | null;
  /** Catalog code of the header-side fastener — etichetta nella legenda colori.*/
  headFastenerCode: string | null;
  /** Catalog code of the joist-side fastener — see headFastenerCode.*/
  joistFastenerCode: string | null;
  /** Catalog code of the inclined fastener — see headFastenerCode.*/
  inclinedFastenerCode: string | null;
}

/**
 * Configuring constants for resolution (PLAN §2.15).
 * In v1 comes from `config/defaults.ts` (see hook T13); parametric seal for
 * tests and for visual calibration.*/
export interface ResolveSceneConfigInput {
  /** Height h of the connector plane (mm).*/
  planeHeight: number;
  /**
   * Lock distance ↔ beam top (SPEC G5, FUTURE parameter; v1 = 0).
   * ACCEPTED by contract but NOT yet applied to beam centers
   * (beamCenterFor doesn't use it — TODO calibration C1/AC-10).*/
  offsetsFromTop: { header: number; joist: number };
  /** Default fastener length (mm) when the item does not provide dimensions.*/
  defaultFastenerLength: number;
}

/**
 * Complete input of `resolveScene` (PLAN §2.15).*/
export interface ResolveSceneInput {
  /** One GlbFeatures per connector (mock for now, useFeatures T4).*/
  featuresList: GlbFeatures[];
  /** Blob URL GLB allineato a featuresList (null → fallback box). */
  glbUrls: (string | null)[];
  /**
   * URL blob of fastener GLBs (from useLockAssets, T13/P6) aligned to
   * `fastenerItemId` of the form. null → parametric fallback box (R6).*/
  fastenerGlbUrls?: { head: string | null; joist: string | null; inclined: string | null };
  /**
   * Wider lock replacing the HEADER half of every pair: its features, holes and GLB are used for the
   * header instance only, the joist half keeps the pair's own lock. null/absent = the pair's lock on both sides.*/
  headerOverride?: { features: GlbFeatures; glbUrl: string | null } | null;
  /** Catalog code of the header override, for the legend only. */
  headerLockCode?: string | null;
  /** Product code aligned (mock for now).*/
  itemIds: (string | null)[];
  /** Catalog code of each connector pair, aligned to itemIds — display only (legenda). */
  itemCodes?: (string | null)[];
  /** Form values ​​(only those used by 3D).*/
  form: ResolveSceneFormInput;
  /** Configuration constants.*/
  config: ResolveSceneConfigInput;
}

/**
 * Calculate the dims of a beam from the form.
 * `header*` → beam header; `joist*` → beam joist (SPEC §3.2).*/
function beamDimsFor(form: ResolveSceneFormInput, role: "header" | "joist"): BeamDims {
  const isHeader = role === "header";
  return {
    // Debug precedente: `?? BEAM_DEFAULTS.*.b` / `?? BEAM_DEFAULTS.*.h`.
    //A beam without B/H filled in should not appear in the viewer.
    b: isHeader ? (form.headerB ?? 0) : (form.joistB ?? 0),
    h: isHeader ? (form.headerH ?? 0) : (form.joistH ?? 0),
    l: DEFAULT_BEAM_LENGTH_MM,
  };
}

function hasBeamDimensions(form: ResolveSceneFormInput, role: "header" | "joist"): boolean {
  const dims = beamDimsFor(form, role);
  return dims.b > 0 && dims.h > 0;
}

/**
 * Resolve the entire scene by applying explicit DEPENDENCY CHAIN
 * STEP 0→6 as Object3D HIERARCHY (kinematic chain).
 *
 * Empty input (`featuresList.length === 0`) → `hasData: false` with structure
 * empty consistent with the `ResolvedScene` type (no render, no errors).
 *
 * @param input Features + form + config.
 * @returns Resolved scene (mm, viewer system) + planeFrame hierarchy.*/
export function resolveScene(input: ResolveSceneInput): ResolvedScene {
  const { featuresList, glbUrls = [], itemIds = [], itemCodes = [], headerOverride = null, headerLockCode = null, form, config } = input;
  const fastenerGlbUrls = input.fastenerGlbUrls ?? { head: null, joist: null, inclined: null };

  //─── Basic parameters (robust defaults with incomplete form) ────────────────
  const planeHeight = config.planeHeight;
  const alphaDeg = form.alphaDeg ?? 0;
  const betaDeg = form.betaDeg ?? 0;
  const orientation: "beam" | "column" = form.headerOrientation === "column" ? "column" : "beam";
  const cutTarget = resolveCutTarget(form.lateralResistanceConfig, orientation);

  //Connector plane (SPEC G1): world root, parallel to Y-Z, normal +X.
  const plane: ResolvedScene["plane"] = {
    origin: [0, planeHeight, 0],
    normal: [1, 0, 0],
    height: planeHeight,
  };
  //ROOT of the powertrain: THREE.Group a y = h (FramePlane).
  const planeFrame = createConnectorPlaneFrame(planeHeight);

  //─── STEP 0 — JSON features (authoring: component on X-Y plane, normals
  //holes along Z). Geometry SSOT; system input.
  // ─────────────────────────────────────────────────────────────────────────

  //─── No connector: the beams still remain viewable ──────────
  if (featuresList.length === 0) {
    return {
      plane,
      planeFrame,
      beams: {
        header: resolveBeam({
          role: "header",
          dims: beamDimsFor(form, "header"),
          orientation,
          cutTarget: "none",
          assemblyThickness: 0,
          assemblyWidth: 0,
          assemblyHeight: 0,
          beamsOrigin: plane.origin,
          skewAlphaDeg: 0,
          skewBetaDeg: 0,
        }),
        joist: resolveBeam({
          role: "joist",
          dims: beamDimsFor(form, "joist"),
          orientation,
          cutTarget: "none",
          assemblyThickness: 0,
          assemblyWidth: 0,
          assemblyHeight: 0,
          beamsOrigin: plane.origin,
          skewAlphaDeg: alphaDeg,
          skewBetaDeg: betaDeg,
        }),
      },
      connectors: [],
      fasteners: [],
      fastenerLegend: [],
      connectorLegend: [],
      headerLockCode: null,
      stagger: 0,
      alphaDeg,
      betaDeg,
      cutTarget: "none",
      hasData: hasBeamDimensions(form, "header") || hasBeamDimensions(form, "joist"),
    };
  }

  //─── STEP 6 (preview) — notch: CutTarget + assembly thickness ────────────
  const firstFeatures = featuresList[0]!;
  const assemblyThickness = firstFeatures.connector_assembly.thickness;
  const assemblyHeight = firstFeatures.connector_assembly.height;
  const requestedCount = form.nConnectors ?? 1;
  const count = clamp(requestedCount, 1, featuresList.length);
  const assemblyWidth = featuresList
    .slice(0, count)
    .reduce((sum, features) => sum + features.connector_assembly.width, 0);
  // A wider header lock keeps the pair's center (both halves interlock there) but widens its footprint:
  // pairs are spaced by the wider half and the header notch follows it. The joist notch keeps its own width.
  const headerLockWidth = headerOverride?.features.connector_assembly.width ?? 0;
  const pairWidths = featuresList
    .slice(0, count)
    .map((features) => Math.max(features.connector_assembly.width, headerLockWidth));
  const headerAssemblyWidth = pairWidths.reduce((sum, width) => sum + width, 0);
  const assemblyGap = resolveAssemblyGap(cutTarget, assemblyThickness);

  //─── STEP 1–4 / op8 — beams (axes, center, quaternion, notch) ───────────
  //Beam header: resolved with orientation from the form (beam/column, SPEC G8).
  const beamsOrigin = [plane.origin[0], plane.origin[1] + assemblyHeight / 2, plane.origin[2]] as Vec3;
  // Joist top below the header top (column header only). The connector keeps
  // its distance from the JOIST top, so relative to the header top (and to the
  // shared beam-top plane everything is placed from) it sits that much lower.
  const joistTopOffsetMm = orientation === "column" ? Math.max(0, form.joistTopOffsetMm ?? 0) : 0;
  const connectorTopDistanceMm = Math.max(0, form.connectorTopDistanceMm ?? 0) + joistTopOffsetMm;
  const headerBeamInput: BeamInput = {
    connectorTopDistance: connectorTopDistanceMm,
    role: "header",
    dims: beamDimsFor(form, "header"),
    orientation,
    cutTarget,
    assemblyThickness,
    assemblyWidth: headerAssemblyWidth,
    assemblyHeight,
    beamsOrigin,
    skewAlphaDeg: 0,
    skewBetaDeg: 0,
  };
  const headerBeam = resolveBeam(headerBeamInput);

  //The joist receives alpha as shear of the geometry ONLY: connector, notch and
  //fasteners remain in the fixed frame of the connector plane.
  const joistBeamNominal = resolveBeam({
    role: "joist",
    dims: beamDimsFor(form, "joist"),
    orientation,
    cutTarget,
    assemblyThickness,
    assemblyWidth,
    assemblyHeight,
    beamsOrigin,
    skewAlphaDeg: alphaDeg,
    skewBetaDeg: betaDeg,
    topOffsetMm: joistTopOffsetMm,
  });

  // ─── STEP 4 — frame joist fisso, alpha applicato SOLO alla mesh ──────────
  //The connector/notch face does not move. We keep the static quota of the
  //parent: brings the connectors to the height of the upper face of the joist.
  //No rotation is applied, so fasteners and connectors remain fixed.
  const pivotOffsetY = beamsOrigin[1] - planeHeight;
  const joistPivot = new THREE.Group();
  joistPivot.name = "joistChain";
  joistPivot.position.set(assemblyGap / 2, pivotOffsetY, 0);
  planeFrame.add(joistPivot);

  //─── op8 — beam groups ──────────────────────── ─────────────────────────
  //JOIST beam: no rigid rotation; the oblique shape is in the geometry.
  const joistBeamGroup = new THREE.Group();
  joistBeamGroup.name = "joistBeam";
  joistBeamGroup.position.set(
    joistBeamNominal.center[0],
    joistBeamNominal.center[1] - pivotOffsetY - planeHeight,
    joistBeamNominal.center[2],
  );
  joistBeamGroup.quaternion.copy(joistBeamNominal.quaternion);
  joistPivot.add(joistBeamGroup);

  //HEADER chain: For gap modes moves as a unit towards -X.
  const headerChain = new THREE.Group();
  headerChain.name = "headerChain";
  headerChain.position.set(-assemblyGap / 2, 0, 0);
  planeFrame.add(headerChain);
  //HEADER beam: daughter of the header chain → DOES NOT rotate with alphaDeg (G6).
  const headerBeamGroup = new THREE.Group();
  headerBeamGroup.name = "headerBeam";
  headerBeamGroup.position.set(headerBeam.center[0], headerBeam.center[1] - planeHeight, headerBeam.center[2]);
  headerBeamGroup.quaternion.copy(headerBeam.quaternion);
  headerChain.add(headerBeamGroup);

  //─── STEP 2/3/4 — connectors (positioning + header derivation) ───────
  //SPEC G4/AC-9: stagger calculated ONCE per features (13.1 in the mockup).
  const stagger = computeStagger(firstFeatures);

  //SPEC G9/AC-4: Z positions of the centers along the connector plane.
  const zOffsets = nConnectorsOffsetsZ(pairWidths);

  const connectors: ConnectorInstance[] = [];
  for (let i = 0; i < count; i++) {
    const features = featuresList[i];
    if (!features) continue;

    //STEP 1–4 are implemented and commented in placeConnectorGroup:
    // conversione Ry(∓90°) → posizionamento joist → derivazione header
    //(Rz180 in the frame). alphaDeg non ruota il frame del connettore, ma
    //CONTRIBUISCE allo shift verticale sotto quando notch+angolo sono attivi.
    //
    //Shift verticale (01-10-2026, richiesta utente: "quando abbiamo la
    //configurazione notch e angolo dobbiamo considerare che il connettore
    //trasla verso il basso") — stessa MAGNITUDINE della formula gia' usata
    //lato backend nei filtri di compatibilita' (`lock_compatibility_filters.go:
    //joistNotchDownwardShift`): `connectorEdgeDistance(lock) * tan(|alphaDeg|)`,
    //attiva SOLO con `LateralResistanceConfig = JoistBeamNotch` (valore 2,
    //`lcDtoModels.LateralResistanceJoistBeamNotch` lato Go,
    //`LATERAL_RESISTANCE_CONFIG.JoistBeamNotch` lato `http/lock_calculator/types.ts`)
    //e alpha non nullo/zero. `connectorEdgeDistance` = `LockModule.ProfileThickness`
    //lato Go = `connector_assembly.thickness` qui (stesso campo, stesso valore
    //di catalogo — vedi doc-comment su `connectorEdgeDistance` in
    //`eta_190831_lock.go`).
    //
    //SEGNO (corretto 01-10-2026, specifica definitiva dall'utente: "quando si
    //ha un valore positivo il connettore deve stare a filo sulla joist ma
    //quando si ha un valore negativo il connettore deve stare a filo con il
    //header"): NON simmetrico — alpha positivo attiva lo shift (a filo col
    //taglio del notch joist), alpha negativo (o zero) NESSUNO shift (resta a
    //filo con l'header, il comportamento di default G5/C1 senza notch). Il
    //backend usa `|alphaDeg|` per un motivo diverso (commento Go: "tilt
    //direction doesn't change the magnitude" — li' serve solo una magnitudine
    //per un controllo di clearance, non una posizione 3D): qui la condizione
    //`alphaDeg > 0` sostituisce sia l'`Math.abs` sia il precedente `!== 0`.
    const JOIST_BEAM_NOTCH_CONFIG = 2;
    const joistNotchDownwardShiftMm =
      form.lateralResistanceConfig === JOIST_BEAM_NOTCH_CONFIG && alphaDeg > 0
        ? features.connector_assembly.thickness * Math.tan(alphaDeg * (Math.PI / 180))
        : 0;
    const connectorInput: ConnectorGroupInput = {
      features,
      itemId: itemIds[i] ?? null,
      glbUrl: glbUrls[i] ?? null,
      headerFeatures: headerOverride?.features ?? null,
      headerGlbUrl: headerOverride?.glbUrl ?? null,
      index: i,
      zCenter: zOffsets[i] ?? 0,
      stagger,
      joistParent: joistPivot,
      headerParent: headerChain,
      pivotOffsetY,
      joistNotchOffset: notchShiftFor(joistBeamNominal) ?? [0, 0, 0],
      headerNotchOffset: notchShiftFor(headerBeam) ?? [0, 0, 0],
      joistNotchDownwardShiftMm,
      connectorTopDistanceMm,
    };
    const { joist, header } = placeConnectorGroup(connectorInput);
    connectors.push(joist, header);
  }

  //─── UPDATE HIERARCHY (powertrain) ───────────────────────────
  // three.js calcola matrixWorld al render; qui "cristallizziamo" i transform
  //world (position/quaternion) immediately after construction, so the components
  //of render and STEP 5 (localToWorld) read correct coordinates.
  planeFrame.updateMatrixWorld(true);
  for (const inst of connectors) {
    const wp = new THREE.Vector3();
    inst.frame.getWorldPosition(wp);
    inst.position = [wp.x, wp.y, wp.z];
    inst.quaternion = inst.frame.getWorldQuaternion(new THREE.Quaternion()).clone();
  }
  //Joist beam: fixed frame center/quaternion; alpha is already in the mesh.
  const joistWorldPos = new THREE.Vector3();
  joistBeamGroup.getWorldPosition(joistWorldPos);
  const joistBeam: ResolvedScene["beams"]["joist"] = {
    ...joistBeamNominal,
    center: [joistWorldPos.x, joistWorldPos.y, joistWorldPos.z],
    quaternion: joistBeamGroup.getWorldQuaternion(new THREE.Quaternion()).clone(),
  };
  const headerWorldPos = new THREE.Vector3();
  headerBeamGroup.getWorldPosition(headerWorldPos);
  const resolvedHeaderBeam: ResolvedScene["beams"]["header"] = {
    ...headerBeam,
    center: [headerWorldPos.x, headerWorldPos.y, headerWorldPos.z],
    quaternion: headerBeamGroup.getWorldQuaternion(new THREE.Quaternion()).clone(),
  };

  //─── STEP 5 — holes → fastener anchors (BEFORE STEP 6; separate Δshift) ───
  //Every hole in EVERY instance (joist and header) becomes an anchor fastener. There
  //position/axis in the world comes from the lock's FRAME (localToWorld /
  //getWorldQuaternion): for the header the mirroring Rz(180°) is already in
  //frame (FIX A — features are NOT denied). Joist fasteners inherit the
  //alphaDeg rotation from pivot.
  // STEP 6 comes AFTER: the Δshift is a separate field (`notchShift`), fasteners
  //positions here are NOT yet translated (SPEC G7/AC-3, doc order=execution).
  const glbByItemId = new Map<string, string | null>();
  if (form.headFastenerItemId) glbByItemId.set(form.headFastenerItemId, fastenerGlbUrls.head);
  if (form.joistFastenerItemId) glbByItemId.set(form.joistFastenerItemId, fastenerGlbUrls.joist);
  if (form.inclinedFastenerItemId) glbByItemId.set(form.inclinedFastenerItemId, fastenerGlbUrls.inclined);

  const fasteners: FastenerAnchor[] = [];
  for (const instance of connectors) {
    for (const hole of instance.features.holes) {
      if (instance.side === "header" && hole.type === "inclined") continue; // skip inclined holes on header

      const fastenerItemId = fastenerItemIdFor(instance.side, hole.type, {
        head: form.headFastenerItemId,
        joist: form.joistFastenerItemId,
        inclined: form.inclinedFastenerItemId,
      });
      //No user choices for this fastener family: do not show the
      //parametric fallback. The fallback remains available only if the fastener is
      //been chosen but your GLB is not available.
      if (!fastenerItemId) continue;
      const realLengthMm = fastenerLengthFor(instance.side, hole.type, {
        head: form.headFastenerLengthMm,
        joist: form.joistFastenerLengthMm,
        inclined: form.inclinedFastenerLengthMm,
      });
      const realDiameterMm = fastenerDiameterFor(instance.side, hole.type, {
        head: form.headFastenerDiameterMm,
        joist: form.joistFastenerDiameterMm,
        inclined: form.inclinedFastenerDiameterMm,
      });
      const fastenerInput: FastenerInput = {
        hole,
        connectorIndex: instance.index,
        side: instance.side,
        frame: instance.frame,
        fastenerItemId,
        //P6: GLB fastener connected from asset loading (T13/useAssets) via
        // fastenerItemId; null → fallback box parametrico (R6).
        glbUrl: fastenerItemId ? (glbByItemId.get(fastenerItemId) ?? null) : null,
        realLengthMm,
        realDiameterMm,
        defaultLength: config.defaultFastenerLength,
        notchShift: [0, 0, 0], //filled by STEP 6 below
        meshQuaternion: instance.meshQuaternion,
      };
      fasteners.push(holeToFastenerAnchor(fastenerInput));
    }
  }

  //─── STEP 6 — the notch is already in the local connector frame ──────────────
  //The CutTarget cuts ONE beam (header or joist): its surface
  //anchor moves by `beam.notch.surfaceOffset` along the normal of the
  //surface (+X, Y-Z connector plane — see `notchShiftFor` in beams.ts).
  //The Δshift is recorded on lock instances AND fasteners; RENDER applies it
  //translating the groups (no mutation of position in model/ — AC-8).
  //The fasteners are calculated after updateMatrixWorld from already translated frames;
  //no additional world shift is needed in the renderer.

  return {
    plane,
    planeFrame,
    beams: { header: resolvedHeaderBeam, joist: joistBeam },
    connectors,
    fasteners,
    fastenerLegend: buildFastenerLegend(
      { head: form.headFastenerItemId, joist: form.joistFastenerItemId, inclined: form.inclinedFastenerItemId },
      { head: form.headFastenerCode, joist: form.joistFastenerCode, inclined: form.inclinedFastenerCode },
      fasteners,
    ),
    connectorLegend: itemCodes.slice(0, count).map((code, i) => ({ index: i, code: code ?? null })),
    headerLockCode: headerOverride ? (headerLockCode ?? null) : null,
    stagger,
    alphaDeg,
    betaDeg,
    cutTarget,
    hasData: true,
  };
}
